import {
  applyBlankTokens,
  blanksToDisplayHtml,
  displayHtmlToBlankTokens,
  blanksToHebrewPreview,
  replaceStyledTokens,
  encodeStyledToken,
} from './memberReportBlanks.js';
import { formatHebrewDate } from '../../../utils/hebrewDate';

export const MERGE_FIELDS = [
  { key: 'displayName', label: 'שם מלא' },
  { key: 'firstName', label: 'שם פרטי' },
  { key: 'lastName', label: 'שם משפחה' },
  { key: 'fatherName', label: 'שם האב' },
  { key: 'personalCode', label: 'קוד אישי' },
  { key: 'memberNumber', label: 'מספר חבר' },
  { key: 'phone', label: 'טלפון' },
  { key: 'phoneAlt', label: 'טלפון נוסף' },
  { key: 'idNumber', label: 'ת.ז.' },
  { key: 'fullAddress', label: 'כתובת מלאה' },
  { key: 'city', label: 'עיר' },
  { key: 'street', label: 'רחוב' },
  { key: 'kolel', label: 'כולל' },
  { key: 'status', label: 'סטטוס' },
  { key: 'role', label: 'תפקיד' },
  { key: 'organization', label: 'ארגון' },
  { key: 'trackChoice', label: 'מסלול' },
  { key: 'system_name', label: 'שם מערכת' },
  { key: 'gregorian_date', label: 'תאריך עברי' },
  { key: 'record_count', label: 'מספר רשומות' },
  { key: 'period_name', label: 'שם מועד' },
  { key: 'registrations_table', label: 'טבלת רישומים' },
  { key: 'grades_table', label: 'טבלת ציונים' },
];

const LABEL_BY_KEY = Object.fromEntries(MERGE_FIELDS.map((f) => [f.key, f.label]));

export const FIELD_LABELS = Object.fromEntries(MERGE_FIELDS.map((f) => [f.key, f.label]));

export const EXPORT_FIELD_OPTIONS = [
  { key: 'personalCode', label: 'קוד אישי' },
  { key: 'memberNumber', label: 'מספר חבר' },
  { key: 'firstName', label: 'שם פרטי' },
  { key: 'lastName', label: 'שם משפחה' },
  { key: 'displayName', label: 'שם מלא' },
  { key: 'fatherName', label: 'שם האב' },
  { key: 'fatherInLawName', label: 'שם חותן' },
  { key: 'phone', label: 'טלפון' },
  { key: 'phoneAlt', label: 'טלפון נוסף' },
  { key: 'idNumber', label: 'ת.ז.' },
  { key: 'marriageYear', label: 'שנת נישואין' },
  { key: 'city', label: 'עיר' },
  { key: 'street', label: 'רחוב' },
  { key: 'buildingNumber', label: 'מספר בניין' },
  { key: 'entrance', label: 'כניסה' },
  { key: 'apartment', label: 'דירה' },
  { key: 'fullAddress', label: 'כתובת מלאה' },
  { key: 'kolel', label: 'כולל' },
  { key: 'kolelMorning', label: 'כולל בוקר' },
  { key: 'kolelAfternoon', label: 'כולל צהריים' },
  { key: 'kolelEvening', label: 'כולל ערב' },
  { key: 'kolelFullDay', label: 'כולל יום שלם' },
  { key: 'trackChoice', label: 'מסלול' },
  { key: 'organization', label: 'ארגון' },
  { key: 'subjects', label: 'מקצועות' },
  { key: 'registered', label: 'נרשם' },
  { key: 'testedBefore', label: 'נבחן בעבר' },
  { key: 'role', label: 'תפקיד' },
  { key: 'status', label: 'סטטוס' },
];

export const DEFAULT_COL_KEYS = [
  'personalCode', 'firstName', 'lastName', 'phone', 'city', 'kolel', 'status',
];

export const GROUP_BY_OPTIONS = [
  { value: 'city', label: 'עיר' },
  { value: 'kolel', label: 'כולל' },
  { value: 'kolelMorning', label: 'כולל בוקר' },
  { value: 'kolelAfternoon', label: 'כולל צהריים' },
  { value: 'kolelEvening', label: 'כולל ערב' },
  { value: 'status', label: 'סטטוס' },
  { value: 'role', label: 'תפקיד' },
  { value: 'trackChoice', label: 'מסלול' },
  { value: 'organization', label: 'ארגון' },
  { value: 'registered', label: 'נרשם' },
];

export function mergeToken(key, styles = {}) {
  return encodeStyledToken(key, styles);
}

export function mergeFieldSpanHtml(key) {
  const label = LABEL_BY_KEY[key] || key;
  return `<span class="box-merge-field" contenteditable="false" data-merge="${key}" style="background:#e3f2fd;color:#1565c0;padding:1px 6px;border-radius:4px;">{${label}}</span>`;
}

function buildFullAddress(record = {}) {
  const parts = [
    record.street,
    record.buildingNumber ? `בניין ${record.buildingNumber}` : null,
    record.entrance ? `כניסה ${record.entrance}` : null,
    record.apartment ? `דירה ${record.apartment}` : null,
    record.city,
  ].filter(Boolean);
  return parts.join(', ');
}

function escapeCell(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function buildRegistrationsTableHtml(regs = []) {
  if (!regs.length) return '<p>אין רישומים</p>';
  const rows = regs.map((r) => {
    const gemara = (r.gemaraSelection || [])
      .map((g) => g.tractate)
      .filter(Boolean)
      .join(', ');
    return `<tr>
      <td>${escapeCell(r.examName || gemara || '—')}</td>
      <td>${escapeCell(r.periodName || '')}</td>
      <td>${escapeCell(r.source || '')}</td>
      <td>${escapeCell(r.createdAt || '')}</td>
    </tr>`;
  }).join('');
  return `<table class="member-merge-table"><thead><tr><th>מבחן / מסכת</th><th>מועד</th><th>מקור</th><th>תאריך</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function buildGradesTableHtml(grades = []) {
  if (!grades.length) return '<p>אין ציונים</p>';
  const rows = grades.map((g) => `<tr>
    <td>${escapeCell(g.examName || g.tractate || '—')}</td>
    <td>${escapeCell(g.periodName || '')}</td>
    <td>${escapeCell(g.score ?? '')}</td>
    <td>${escapeCell(g.enteredAt || '')}</td>
  </tr>`).join('');
  return `<table class="member-merge-table"><thead><tr><th>מבחן / מסכת</th><th>מועד</th><th>ציון</th><th>תאריך</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function applyMemberMergeTemplateClient(html, record = {}, extras = {}) {
  if (!html) return '';
  const valuesByKey = {
    firstName: record.firstName || '',
    lastName: record.lastName || '',
    displayName: record.displayName || [record.firstName, record.lastName].filter(Boolean).join(' '),
    personalCode: record.personalCode != null ? String(record.personalCode) : '',
    memberNumber: record.memberNumber || '',
    fatherName: record.fatherName || '',
    phone: record.phone || '',
    phoneAlt: record.phoneAlt || '',
    idNumber: record.idNumber || '',
    city: record.city || '',
    street: record.street || '',
    kolel: record.kolel || '',
    fullAddress: record.fullAddress || buildFullAddress(record),
    status: record.status || '',
    role: record.role || '',
    organization: record.organization || '',
    trackChoice: record.trackChoice || '',
    system_name: extras.system_name || '',
    gregorian_date: extras.gregorian_date || formatHebrewDate(new Date()),
    record_count: extras.record_count != null ? String(extras.record_count) : '',
    period_name: extras.period_name || '',
    registrations_table: extras.registrations_table || '',
    grades_table: extras.grades_table || '',
  };
  const out = replaceStyledTokens(html, (key) => valuesByKey[key] ?? '');
  return applyBlankTokens(out);
}

export function tokensToDisplayHtml(html = '') {
  let out = blanksToDisplayHtml(String(html));
  out = out.replace(/\{\{(?!blank:)([^}|]+)(?:\|([^}]+))?\}\}/g, (_, key) => mergeFieldSpanHtml(key));
  return out;
}

export function displayHtmlToTokens(html = '') {
  let out = displayHtmlToBlankTokens(String(html));
  out = out.replace(
    /<span[^>]*data-merge="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi,
    (_, key) => mergeToken(key)
  );
  for (const { key, label } of MERGE_FIELDS) {
    out = out.replace(new RegExp(`\\{${label}\\}`, 'g'), mergeToken(key));
  }
  return out;
}

export function tokensToHebrewPreview(html = '') {
  let out = blanksToHebrewPreview(String(html));
  out = out.replace(/\{\{(?!blank:)([^}|]+)(?:\|([^}]+))?\}\}/g, (_, key) => (
    `{${LABEL_BY_KEY[key] || key}}`
  ));
  return out;
}

export const DEFAULT_CARD_TEMPLATE = [
  '<p><strong>כרטיס נבחן</strong></p>',
  '<p>{{displayName}} · קוד {{personalCode}}</p>',
  '<p>{{fullAddress}}</p>',
  '<p>טלפון: {{phone}}</p>',
  '<p>כולל: {{kolel}}</p>',
  '<p><br></p>',
  '<p><strong>רישומים</strong></p>',
  '<p>{{registrations_table}}</p>',
  '<p><strong>ציונים</strong></p>',
  '<p>{{grades_table}}</p>',
  '<p><br></p>',
  '<p>{{blank:signature}}</p>',
].join('');

export const DEFAULT_LIST_TEMPLATE = [
  '<p>דוח חברים — {{gregorian_date}}</p>',
  '<p>{{record_count}} רשומות</p>',
].join('');

export const DEFAULT_BREAKDOWN_TEMPLATE = [
  '<p>פילוח חברים — {{gregorian_date}}</p>',
  '<p>{{record_count}} רשומות</p>',
].join('');

export function defaultTemplateForType(targetType) {
  if (targetType === 'list') return DEFAULT_LIST_TEMPLATE;
  if (targetType === 'breakdown') return DEFAULT_BREAKDOWN_TEMPLATE;
  return DEFAULT_CARD_TEMPLATE;
}
